import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import BadgesGrid from "@/components/BadgesGrid";
import { Check, Circle, X, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function GettingStarted() {
  const [onb, setOnb] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/engagement/onboarding").then((r) => {
    setOnb(r.data);
    // Notify the sidebar so it can recompute visibility live.
    window.dispatchEvent(new CustomEvent("onboarding:updated"));
  });
  useEffect(() => { load(); }, []);

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post("/engagement/onboarding/dismiss");
      toast.success("Hidden from sidebar — you can revisit anytime");
      await load();
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.post("/engagement/onboarding/reset");
      await load();
    } finally { setBusy(false); }
  };

  if (!onb) return <div className="text-stone-500">Loading…</div>;
  const pct = Math.round((onb.completed / onb.total) * 100);

  return (
    <div className="space-y-10 max-w-3xl" data-testid="getting-started-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">For new beekeepers</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">Getting started</h1>
        <p className="text-stone-600 mt-3 max-w-xl">
          A short trail to set up your apiary. Each step unlocks something useful — and a badge to remember it by.
        </p>
      </div>

      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-amber-700" />
            <div>
              <div className="font-display text-2xl font-medium" data-testid="onboarding-progress-count">{onb.completed} / {onb.total} done</div>
              <div className="text-xs text-amber-900">{pct}% complete</div>
            </div>
          </div>
          {onb.completed === onb.total ? (
            <Button variant="outline" onClick={dismiss} disabled={busy} className="rounded-full border-amber-300" data-testid="onboarding-dismiss-button">
              <X className="w-4 h-4 mr-1.5" /> Hide from sidebar
            </Button>
          ) : onb.dismissed && (
            <Button variant="ghost" onClick={reset} disabled={busy} className="rounded-full text-amber-800" data-testid="onboarding-reset-button">
              Re-show in sidebar
            </Button>
          )}
        </div>
        <div className="mt-3 h-2 bg-amber-200/40 rounded-full overflow-hidden">
          <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <ul className="space-y-3">
        {onb.items.map((it, idx) => (
          <li
            key={it.key}
            className={`rounded-2xl border p-5 flex items-start gap-4 ${
              it.done ? "border-emerald-200 bg-emerald-50/30" : "border-stone-200 bg-white"
            }`}
            data-testid={`onboarding-item-${it.key}`}
          >
            <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${
              it.done ? "bg-emerald-500 text-white" : "bg-stone-100 text-stone-400"
            }`}>
              {it.done ? <Check className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-[0.15em] text-stone-400 font-semibold">Step {idx + 1}</span>
                {it.done && <span className="text-[10px] uppercase tracking-[0.12em] text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Done</span>}
              </div>
              <div className="font-display text-xl font-medium mt-1">{it.title}</div>
              <p className="text-sm text-stone-600 mt-1">{it.description}</p>
              {!it.done && (
                <Link to={it.cta_url} className="inline-flex items-center gap-1 text-sm text-amber-700 hover:underline mt-2" data-testid={`onboarding-cta-${it.key}`}>
                  Go <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>

      <section>
        <h2 className="font-display text-2xl font-medium mb-2">Badges</h2>
        <p className="text-sm text-stone-600 mb-4">Earn these as your apiary grows. Earned badges appear on your public share page.</p>
        <BadgesGrid />
      </section>
    </div>
  );
}
